<template>
  <SoRadioCards v-model="status" class="mt-10 mb-14" :mandatory="false">
    <SoRadioCardChart
      v-for="filter in topFilters"
      :key="filter.name"
      :title="filter.label"
      :value="filter.name"
      :percentage="filter.percentage"
      :type="filter.type"
      :is-loading="summaryQuery?.isLoading"
    >
      <span class="text-h2">
        <template v-if="filter.name === 'total'">
          {{ summaryQuery?.data?.total }}
        </template>
        <template v-else>
          {{ filter.count }} - {{ filter.percentage }}%
        </template>
      </span>
    </SoRadioCardChart>
  </SoRadioCards>

  <SoAlert
    v-if="recordsQuery?.error"
    title="Failed to render direct debit reviews table"
    data-testid="direct-debit-review-error"
  />

  <SoDataTableServer
    v-else
    ref="dataTable"
    v-model="selectedRecordIds"
    v-model:options="tableOptions"
    :headers="tableHeaders"
    :data="tableData"
    :default-items-per-page="10"
    :is-item-selectable="isItemSelectable"
    select-key="id"
    :select-all-disabled="!enableSelectAll"
    data-testid="direct-debit-review-list-table"
    card
    selectable
    @fetch="fetchPaymentReview"
    @all-selected="onAllSelected"
  >
    <template #tableActions>
      <SoButton
        data-testid="direct-debit-review-download-btn"
        :disabled="
          isDownloadingRecords || !tableData.totalItems || tableData.isLoading
        "
        :loading="isDownloadingRecords"
        title="Download"
        prepend-icon="download"
        large
        @click="downloadRecords"
      />

      <SoButton
        data-testid="direct-debit-review-remove-btn"
        :title="
          'Remove' +
          (selectedRecordIds.length ? ` (${selectedRecordIds.length})` : '')
        "
        :disabled="!canDeleteRecords"
        prepend-icon="delete"
        large
        @click="showRemoveRecordsModal = true"
      />
      <SoTooltip
        :disabled="totalItemsSelected < MAX_SELECTED_RECORDS"
        text="
          A maximum of 5000 records may be retried at once.
          Please filter the number of records down further before
          attempting to retry
        "
        max-width="300"
      >
        <SoButton
          data-testid="direct-debit-review-reprocess-btn"
          :title="
            'Reprocess' + (totalItemsSelected ? ` (${totalItemsSelected})` : '')
          "
          :disabled="!canReprocessRecords"
          type="primary"
          large
          @click="showReprocessModal = true"
        />
      </SoTooltip>
    </template>

    <template
      v-if="
        (showReprocessTotalRecordsOption ||
          reprocessMethod === ReprocessMethod.ByFilters) &&
        hasMoreRecordsToSelect
      "
      #prependBody="{ totalItems }"
    >
      <div class="rounded bg-border my-2 py-3 text-center font-weight-regular">
        <template v-if="reprocessMethod === ReprocessMethod.ByFilters">
          All <span class="font-weight-medium">{{ totalItems }}</span> review
          ID's are selected.
          <a
            class="text-primary text-decoration-none font-weight-medium"
            href="javascript:void(0);"
            @click="reprocessMethod = ReprocessMethod.ByIds"
          >
            Undo
          </a>
        </template>

        <template v-else>
          All
          <span class="font-weight-medium">{{ selectedRecordIds.length }}</span>
          review ID's on this page are selected.
          <a
            class="text-primary text-decoration-none font-weight-medium"
            href="javascript:void(0);"
            @click="reprocessMethod = ReprocessMethod.ByFilters"
          >
            Select all {{ totalItems }} {{ status }} review ID's
          </a>
        </template>
      </div>
    </template>

    <template #item.failureReasonMapped="{ internalItem }">
      <div
        v-if="internalItem.columns.failureReasonMapped"
        class="failure-reason pa-2 text-body-2"
        :class="`failure-reason--${internalItem.columns.failureReasonMapped.type}`"
        data-testid="direct-debit-failure-reason"
      >
        {{ internalItem.columns.failureReasonMapped.text }}
      </div>
      <span v-else>-</span>
    </template>
    <template #itemActions="{ item }">
      <SoButton
        data-testid="direct-debit-table-action"
        type="table-action"
        title="View"
        @click="viewRecord(item.id)"
      />
    </template>
  </SoDataTableServer>

  <OpsPaymentReviewRecordModal v-if="viewRecordId" v-model="viewRecordId" />
  <OpsReprocessReviewRecordModal
    v-if="showReprocessModal"
    v-model="showReprocessModal"
    :record-ids="selectedRecordIds"
    :record-count="totalItemsSelected"
    :payment-review-id="paymentReviewId"
    :reprocess-method="reprocessMethod"
    :filters="mapFilters(tableOptions.filters)"
    @success="handleReprocessSuccessEvent"
  />
  <OpsDirectDebitReviewRemoveModal
    v-if="showRemoveRecordsModal"
    v-model="showRemoveRecordsModal"
    :record-ids="selectedRecordIds"
    @success="handleRemoveSuccessEvent"
  />
</template>

<script lang="ts">
  import OpsReprocessReviewRecordModal from "@/views/operations/directDebitReviews/modals/OpsReprocessReviewRecordModal.vue"
  import OpsDirectDebitReviewRemoveModal from "@/views/operations/directDebitReviews/modals/OpsDirectDebitReviewRemoveModal.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoRadioCardChart from "@/components/SoRadioCardChart.vue"
  import SoRadioCards from "@/components/SoRadioCards.vue"
  import SoDataTableServer, {
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { SoDataTableItem } from "@/types/components/SoDataTable"
  import { safeDayjs } from "@/helpers/dateHelper"
  import { errorHandler, errorReporter } from "@/services/ErrorService"
  import { PaymentReviewRecordStatus } from "@/models/operations/paymentReviewRecord/paymentReviewRecord.types"
  import {
    PaymentReviewRecordFilters,
    PaymentReviewService,
    RecordsQueryResult,
  } from "@/services/operations/PaymentReviewService"
  import { PaymentReviewMutator } from "@/mutators/paymentReview/PaymentReviewMutator"
  import { DownloadPaymentReviewsResponse } from "@/api/backofficeRest/generated/model"
  import { Status, StatusType } from "@/types/Status"
  import OpsPaymentReviewRecordModal from "@/views/operations/directDebitReviews/modals/OpsPaymentReviewRecordModal.vue"
  import { capitalize, cloneDeep } from "lodash-es"
  import { defineComponent } from "vue"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { QueryResult } from "@/api/utils/types"
  import {
    editableStatuses,
    MAX_SELECTED_RECORDS,
    ReprocessMethod,
    statuses,
    tableFilters,
    TableFilters,
    tableHeaders,
    topFilters,
  } from "../opsDirectDebitReviewConfig"
  import SoTooltip from "@/components/SoTooltip.vue"
  import { FAILURE_REASONS_MAPPED } from "@/constants/paymentReview"

  const GET_SUMMARY_POLLING_INTERVAL_MS = 5000

  export default defineComponent({
    components: {
      OpsReprocessReviewRecordModal,
      OpsDirectDebitReviewRemoveModal,
      SoAlert,
      SoDataTableServer,
      SoButton,
      SoRadioCards,
      SoRadioCardChart,
      OpsPaymentReviewRecordModal,
      SoTooltip,
    },
    props: {
      paymentReviewId: {
        type: Number,
        required: true,
      },
    },
    emits: ["update:paymentReviewId"],
    data() {
      return {
        MAX_SELECTED_RECORDS,
        ReprocessMethod,
        tableHeaders,
        tableOptions: {
          filters: tableFilters,
          orderBy: {
            created_at: "desc",
          },
        } as Pick<SoDataTableOptions<TableFilters>, "filters" | "orderBy">,
        selectedRecordIds: [] as number[],
        statuses,
        recordsQuery: null as QueryResult<RecordsQueryResult> | null,
        summaryQuery: PaymentReviewService.getSummary(this.paymentReviewId),
        fetchSummaryInterval: null as ReturnType<typeof setInterval> | null,
        showPaymentReviewRecordModal: false,
        viewRecordId: null as number | null,
        reprocessMethod: ReprocessMethod.ByIds,
        showReprocessTotalRecordsOption: false,
        showReprocessModal: false,
        showRemoveRecordsModal: false,
        isDownloadingRecords: false,
        snackbarStore: useSnackbarStore(),
      }
    },

    computed: {
      canReprocessRecords(): boolean {
        return (
          !this.tableData.isLoading &&
          this.totalItemsSelected > 0 &&
          this.totalItemsSelected <= MAX_SELECTED_RECORDS
        )
      },
      canDeleteRecords(): boolean {
        return (
          !this.tableData.isLoading &&
          this.reprocessMethod === ReprocessMethod.ByIds &&
          this.selectedRecordIds.length > 0
        )
      },
      hasMoreRecordsToSelect() {
        return this.selectedRecordIds.length < this.tableData.totalItems
      },
      totalItemsSelected(): number {
        return this.reprocessMethod === ReprocessMethod.ByIds
          ? this.selectedRecordIds.length
          : this.tableData.totalItems
      },
      topFilters() {
        const totalCount = this.summaryQuery?.data?.total ?? 0
        return topFilters.map((filter) => {
          const count = this.summaryQuery?.data?.[filter.name] ?? 0
          const ratio = totalCount ? count / totalCount : 0
          const percentage = Math.round(ratio * 100)
          return { ...filter, count, percentage }
        })
      },
      status: {
        get(): string | null {
          const value = this.tableOptions.filters.status.value
          return value === null ? "total" : value
        },
        set(value) {
          this.tableOptions.filters.status.value =
            value === "total" ? null : value
        },
      },
      tableData() {
        return {
          isLoading: !!this.recordsQuery?.isFetching,
          totalItems: this.recordsQuery?.data?.totalCount ?? 0,
          items:
            this.recordsQuery?.data?.records?.map((record) => ({
              ...record,
              failureReasonMapped: record.failureReasonMapped,
              status: this.getStatus(record.status),
            })) ?? [],
        }
      },
      activeFailureCodes() {
        return this.recordsQuery?.data?.activeFailureCodes
      },
      enableSelectAll() {
        if (!this.status) {
          return false
        }
        return editableStatuses.includes(
          this.status as PaymentReviewRecordStatus,
        )
      },
    },

    watch: {
      paymentReviewId() {
        this.filterPaymentReviewTable()
      },
      status() {
        this.filterPaymentReviewTable()
      },
      activeFailureCodes(failureCodes) {
        if (!failureCodes) return

        const failureReasonOptions = {}

        for (const code in FAILURE_REASONS_MAPPED) {
          if (failureCodes.includes(code)) {
            const label = FAILURE_REASONS_MAPPED[code].text
            failureReasonOptions[code] = label
          }
        }

        const filters = cloneDeep(this.tableOptions.filters)
        filters.reason.options = failureReasonOptions
        // Remove any failure codes that are no longer available
        filters.reason.value = filters.reason.value.filter((v) =>
          failureCodes.includes(v),
        )

        const dataTable = this.$refs.dataTable as InstanceType<
          typeof SoDataTableServer
        >
        dataTable.setOptions({ filters }, false)
      },
    },

    created() {
      this.summaryInterval()
    },
    beforeUnmount() {
      this.clearSummaryInterval()
    },

    methods: {
      clearSummaryInterval() {
        if (this.fetchSummaryInterval) {
          clearInterval(this.fetchSummaryInterval)
        }
      },
      summaryInterval() {
        this.clearSummaryInterval()

        this.fetchSummaryInterval = setInterval(
          this.summaryQuery.refetch,
          GET_SUMMARY_POLLING_INTERVAL_MS,
        )
      },
      fetchSummary() {
        if (!this.paymentReviewId) return

        this.summaryQuery = PaymentReviewService.getSummary(
          this.paymentReviewId,
        )

        this.summaryInterval()
      },
      async refetchTableData(resetPagination: boolean) {
        this.selectedRecordIds = []
        const dataTable = this.$refs.dataTable as InstanceType<
          typeof SoDataTableServer
        >
        return dataTable.refetch(resetPagination)
      },
      async refetchAll() {
        this.recordsQuery?.refetch()
        this.refetchTableData(true)
        this.fetchSummary()
      },
      async handleReprocessSuccessEvent() {
        this.snackbarStore.addSuccess(
          "Reprocess request successfully submitted",
        )
        this.refetchAll()
      },
      async handleRemoveSuccessEvent() {
        this.snackbarStore.addSuccess("Records successfully removed")
        this.refetchAll()
      },
      onAllSelected(selected: boolean) {
        this.showReprocessTotalRecordsOption = selected

        if (!selected) {
          this.reprocessMethod = ReprocessMethod.ByIds
        }
      },
      isItemSelectable(item: SoDataTableItem) {
        if (!this.status) {
          return false
        }

        const status = item.status as { value: PaymentReviewRecordStatus }
        return editableStatuses.includes(status.value)
      },
      getStatus(status: PaymentReviewRecordStatus) {
        let type: StatusType = "default"
        if (status === "completed") type = "normal"
        else if (status === "skipped") type = "caution"
        else if (status === "failed") type = "critical"
        else if (status === "pending") type = "neutral"

        return {
          value: status,
          props: {
            status: new Status(type, capitalize(status)),
          },
        }
      },
      mapFilters(filters: TableFilters): PaymentReviewRecordFilters {
        return {
          status: filters.status.value ?? undefined,
          errorCodes: filters.reason.value ?? undefined,
          from: safeDayjs(filters.date.value.from),
          to: safeDayjs(filters.date.value.to),
          nextPaymentDateFrom: safeDayjs(filters.nextPaymentDate.value.from),
          nextPaymentDateTo: safeDayjs(filters.nextPaymentDate.value.to),
          changedAmountMin: filters.changedAmount.value.min ?? undefined,
          changedAmountMax: filters.changedAmount.value.max ?? undefined,
          lastRetryDateFrom: safeDayjs(filters.lastRetryDate.value.from),
          lastRetryDateTo: safeDayjs(filters.lastRetryDate.value.to),
        }
      },
      fetchPaymentReview({
        page,
        pageSize,
        filters,
        orderBy,
      }: SoDataTableOptions<TableFilters>) {
        this.recordsQuery = PaymentReviewService.getRecords(
          this.paymentReviewId,
          this.mapFilters(filters),
          page,
          pageSize,
          orderBy,
        )
      },
      filterPaymentReviewTable() {
        this.fetchPaymentReview({
          filters: this.tableOptions.filters,
          page: 1,
          pageSize: 10,
          orderBy: this.tableOptions.orderBy,
        } as SoDataTableOptions<TableFilters>)
        this.refetchAll()
      },
      async downloadRecords() {
        this.isDownloadingRecords = true
        try {
          const { mutateAsync } = PaymentReviewMutator.downloadRecords()
          const response = (await mutateAsync({
            paymentReviewId: this.paymentReviewId,
            filters: this.mapFilters(this.tableOptions.filters),
          })) as DownloadPaymentReviewsResponse

          if (!response?.fileUrl) {
            throw new Error("Failed to download records.")
          }
          window.open(response.fileUrl)
        } catch (error) {
          this.snackbarStore.addError(
            errorHandler.userMessage(error, "Failed to download records."),
          )
          errorReporter.report(error)
          console.error(error)
        }
        this.isDownloadingRecords = false
      },
      viewRecord(id: number) {
        this.viewRecordId = id
      },
    },
  })
</script>
<style lang="css" scoped>
  .failure-reason {
    border-radius: var(--theme-radius);
    background-color: rgb(var(--v-theme-default));
    min-width: 150px;
    max-width: 300px;
    line-height: 1;
  }
  .failure-reason--warning {
    background-color: rgb(var(--v-theme-caution));
    color: rgb(var(--v-theme-on-caution));
  }
  .failure-reason--danger {
    background-color: rgb(var(--v-theme-critical));
    color: rgb(var(--v-theme-on-critical));
  }
</style>
